import { createContext, useContext, useRef, useState } from "react";
import { toast } from "sonner";
import { Building2, FileVideo, ImageIcon, Loader2, Paperclip, Send, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PRIORITIES, slugify } from "@/lib/constants";

const LeadModalContext = createContext(null);

export const useLeadModal = () => useContext(LeadModalContext);

const INITIAL = {
  name: "",
  phone: "",
  email: "",
  profile: "",
  city: "",
  address: "",
  agency_name: "",
  priority: "",
  message: "",
};

const AGENCY_INITIAL = { company: "", name: "", email: "", phone: "" };

const EMPTY_SLOTS = [
  { date: "", time: "" },
  { date: "", time: "" },
  { date: "", time: "" },
];

const PRIORITY_STYLES = {
  Urgente: "border-red-600 bg-red-600 text-white",
  Média: "border-amber-500 bg-amber-500 text-white",
  Baixa: "border-slate-500 bg-slate-500 text-white",
};

const ACCEPT = ".jpg,.jpeg,.png,.mp4,.mov,.avi";
const MAX_FILES = 8;
const MAX_SIZE_MB = 50;

const formatSize = (bytes) =>
  bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : `${Math.round(bytes / 1024)} KB`;

const isVideo = (name) => /\.(mp4|mov|avi)$/i.test(name);

export const LeadModalProvider = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("service");
  const [form, setForm] = useState(INITIAL);
  const [agencyForm, setAgencyForm] = useState(AGENCY_INITIAL);
  const [slots, setSlots] = useState(EMPTY_SLOTS);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);

  const openModal = (profile = "", requestedMode = "service") => {
    setMode(requestedMode);
    if (profile) setForm((f) => ({ ...f, profile }));
    setOpen(true);
  };

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target ? e.target.value : e }));
  const setAgency = (key) => (e) =>
    setAgencyForm((f) => ({ ...f, [key]: e.target ? e.target.value : e }));

  const setSlot = (index, key, value) =>
    setSlots((prev) => prev.map((s, i) => (i === index ? { ...s, [key]: value } : s)));

  const addFiles = (fileList) => {
    const incoming = Array.from(fileList || []);
    const valid = [];
    for (const file of incoming) {
      if (!/\.(jpe?g|png|mp4|mov|avi)$/i.test(file.name)) {
        toast.error(`Formato não aceito: ${file.name}`, {
          description: "Fotos em JPEG/PNG e vídeos em MP4, MOV ou AVI.",
        });
        continue;
      }
      if (file.size > MAX_SIZE_MB * 1024 * 1024) {
        toast.error(`Arquivo muito grande: ${file.name}`, {
          description: `Limite de ${MAX_SIZE_MB} MB por arquivo.`,
        });
        continue;
      }
      valid.push(file);
    }
    setFiles((prev) => {
      const next = [...prev, ...valid];
      if (next.length > MAX_FILES) {
        toast.error(`Máximo de ${MAX_FILES} arquivos por solicitação.`);
        return next.slice(0, MAX_FILES);
      }
      return next;
    });
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeFile = (index) => setFiles((prev) => prev.filter((_, i) => i !== index));

  const submitAgency = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      await api.post("/leads/agency", {
        company: agencyForm.company.trim(),
        name: agencyForm.name.trim(),
        email: agencyForm.email.trim(),
        phone: agencyForm.phone.trim(),
      });
      toast.success("Contato enviado!", {
        description: "Nossa equipe comercial fala com a sua imobiliária em breve.",
      });
      setAgencyForm(AGENCY_INITIAL);
      setOpen(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (!form.priority) {
      toast.error("Selecione a prioridade da solicitação.");
      return;
    }
    const filledSlots = slots.filter((s) => s.date && s.time);
    setLoading(true);
    try {
      const data = new FormData();
      data.append("name", form.name.trim());
      data.append("phone", form.phone.trim());
      if (form.email.trim()) data.append("email", form.email.trim());
      data.append("profile", form.profile);
      data.append("city", form.city.trim());
      data.append("address", form.address.trim());
      if (form.profile === "Inquilino" && form.agency_name.trim()) {
        data.append("agency_name", form.agency_name.trim());
      }
      data.append("priority", form.priority);
      data.append("schedule", JSON.stringify(filledSlots));
      data.append("message", form.message.trim());
      files.forEach((file) => data.append("files", file));
      await api.post("/leads/service", data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Solicitação enviada!", {
        description: "A Resolv recebeu sua demanda e entra em contato para confirmar a visita.",
      });
      setForm(INITIAL);
      setSlots(EMPTY_SLOTS);
      setFiles([]);
      setOpen(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <LeadModalContext.Provider value={{ openModal }}>
      {children}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="max-h-[94vh] max-w-xl gap-0 overflow-y-auto rounded-2xl p-5"
          data-testid="lead-modal"
        >
          {mode === "agency" ? (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3 font-display text-2xl font-bold text-petrol">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand/10 text-brand">
                    <Building2 className="h-5 w-5" />
                  </span>
                  Resolv para imobiliárias
                </DialogTitle>
                <DialogDescription>
                  Deixe seus dados e nossa equipe comercial apresenta a operação Resolv para a
                  sua imobiliária.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={submitAgency} className="mt-2 grid grid-cols-1 gap-4" data-testid="agency-form">
                <div className="space-y-1.5">
                  <Label htmlFor="agency-company">Nome da imobiliária*</Label>
                  <Input
                    id="agency-company"
                    data-testid="agency-company-input"
                    required
                    minLength={2}
                    value={agencyForm.company}
                    onChange={setAgency("company")}
                    placeholder="Imobiliária Exemplo"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="agency-name">Nome do responsável*</Label>
                  <Input
                    id="agency-name"
                    data-testid="agency-name-input"
                    required
                    minLength={2}
                    value={agencyForm.name}
                    onChange={setAgency("name")}
                    placeholder="Quem fala com a gente"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="agency-email">E-mail*</Label>
                  <Input
                    id="agency-email"
                    data-testid="agency-email-input"
                    type="email"
                    required
                    value={agencyForm.email}
                    onChange={setAgency("email")}
                    placeholder="voce@imobiliaria.com.br"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="agency-phone">Telefone / WhatsApp*</Label>
                  <Input
                    id="agency-phone"
                    data-testid="agency-phone-input"
                    required
                    minLength={8}
                    value={agencyForm.phone}
                    onChange={setAgency("phone")}
                    placeholder="(48) 99999-9999"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="agency-submit-button"
                  className="h-12 rounded-full bg-brand font-semibold text-white transition-colors hover:bg-brand-700"
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      Quero conhecer a Resolv
                      <Send className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>
              </form>
            </>
          ) : (
            <>
              <DialogHeader className="space-y-0.5">
                <DialogTitle className="font-display text-xl font-bold text-petrol">
                  Solicitar atendimento
                </DialogTitle>
                <DialogDescription className="text-xs">
                  Conte o que precisa ser resolvido. A Resolv organiza o resto.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={submit} className="mt-0.5 grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                <div className="space-y-1">
                  <Label htmlFor="lead-name" className="text-xs">Nome*</Label>
                  <Input
                    id="lead-name"
                    data-testid="lead-modal-name-input"
                    required
                    minLength={2}
                    value={form.name}
                    onChange={set("name")}
                    placeholder="Seu nome"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="lead-phone" className="text-xs">Telefone / WhatsApp*</Label>
                  <Input
                    id="lead-phone"
                    data-testid="lead-modal-phone-input"
                    required
                    minLength={8}
                    value={form.phone}
                    onChange={set("phone")}
                    placeholder="(48) 99999-9999"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="lead-email" className="text-xs">E-mail</Label>
                  <Input
                    id="lead-email"
                    data-testid="lead-modal-email-input"
                    type="email"
                    value={form.email}
                    onChange={set("email")}
                    placeholder="voce@email.com"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="lead-city" className="text-xs">Cidade*</Label>
                  <Input
                    id="lead-city"
                    data-testid="lead-modal-city-input"
                    required
                    minLength={2}
                    value={form.city}
                    onChange={set("city")}
                    placeholder="Florianópolis - SC"
                  />
                </div>
                <div className="space-y-1 sm:col-span-2">
                  <Label htmlFor="lead-address" className="text-xs">Endereço do imóvel*</Label>
                  <Input
                    id="lead-address"
                    data-testid="lead-modal-address-input"
                    required
                    minLength={3}
                    value={form.address}
                    onChange={set("address")}
                    placeholder="Rua, número, bairro e apto"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Eu sou*</Label>
                  <Select required value={form.profile} onValueChange={set("profile")}>
                    <SelectTrigger data-testid="lead-modal-profile-select">
                      <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Proprietário">Proprietário</SelectItem>
                      <SelectItem value="Inquilino">Inquilino</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Prioridade da solicitação*</Label>
                  <div className="flex flex-wrap gap-1.5" data-testid="lead-modal-priority-picker">
                    {PRIORITIES.map((p) => {
                      const active = form.priority === p;
                      return (
                        <button
                          type="button"
                          key={p}
                          onClick={() => setForm((f) => ({ ...f, priority: p }))}
                          data-testid={`lead-modal-priority-${slugify(p)}`}
                          aria-pressed={active}
                          className={`rounded-full border px-3.5 py-1 text-xs font-semibold transition-[background-color,border-color,color] duration-200 ${
                            active
                              ? PRIORITY_STYLES[p]
                              : "border-slate-300 bg-white text-slate-600 hover:border-slate-400"
                          }`}
                        >
                          {p}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {form.profile === "Inquilino" && (
                  <div className="space-y-1 sm:col-span-2">
                    <Label htmlFor="lead-agency" className="text-xs">
                      De qual imobiliária é o imóvel?*
                    </Label>
                    <Input
                      id="lead-agency"
                      data-testid="lead-modal-agency-input"
                      required
                      minLength={2}
                      value={form.agency_name}
                      onChange={set("agency_name")}
                      placeholder="Nome da imobiliária responsável pelo aluguel"
                    />
                  </div>
                )}

                <div className="space-y-1.5 sm:col-span-2">
                  <div className="flex flex-wrap items-baseline justify-between gap-x-2">
                    <Label className="text-xs">
                      Disponibilidade para a visita{" "}
                      <span className="font-normal text-slate-400">(opcional)</span>
                    </Label>
                    <p className="text-[0.65rem] text-slate-400">
                      Até 3 opções — pode repetir a data com horários diferentes
                    </p>
                  </div>
                  <div className="grid gap-1.5">
                    {slots.map((slot, i) => (
                      <div key={i} className="grid grid-cols-[56px_1fr_1fr] items-center gap-2">
                        <span className="font-mono text-[0.6rem] font-semibold uppercase tracking-wider text-slate-400">
                          Opção {i + 1}
                        </span>
                        <Input
                          type="date"
                          data-testid={`lead-modal-date-${i}`}
                          value={slot.date}
                          onChange={(e) => setSlot(i, "date", e.target.value)}
                          className="h-8 text-xs"
                        />
                        <Input
                          type="time"
                          data-testid={`lead-modal-time-${i}`}
                          value={slot.time}
                          onChange={(e) => setSlot(i, "time", e.target.value)}
                          className="h-8 text-xs"
                        />
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-1 sm:col-span-2">
                  <Label htmlFor="lead-message" className="text-xs">O que precisa ser resolvido?*</Label>
                  <Textarea
                    id="lead-message"
                    data-testid="lead-modal-message-input"
                    required
                    minLength={5}
                    rows={2}
                    value={form.message}
                    onChange={set("message")}
                    placeholder="Ex.: vazamento no banheiro do apt 42, vistoria de desocupação..."
                  />
                </div>

                <div className="space-y-1 sm:col-span-2">
                  <Label className="text-xs">
                    Fotos e vídeos do problema{" "}
                    <span className="font-normal text-slate-400">
                      (JPEG/PNG • MP4/MOV/AVI • até {MAX_SIZE_MB} MB cada)
                    </span>
                  </Label>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    data-testid="lead-modal-attach-button"
                    className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 px-4 py-2.5 text-center transition-colors duration-300 hover:border-brand hover:bg-brand/5"
                  >
                    <Paperclip className="h-4 w-4 text-slate-400" />
                    <span className="text-xs font-medium text-slate-600">
                      Toque para anexar fotos ou vídeos
                    </span>
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={ACCEPT}
                    multiple
                    className="hidden"
                    data-testid="lead-modal-file-input"
                    onChange={(e) => addFiles(e.target.files)}
                  />
                  {files.length > 0 && (
                    <ul className="mt-1.5 space-y-1.5" data-testid="lead-modal-attachments-list">
                      {files.map((file, i) => (
                        <li
                          key={`${file.name}-${i}`}
                          className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white px-3 py-1.5"
                        >
                          {isVideo(file.name) ? (
                            <FileVideo className="h-4 w-4 shrink-0 text-brand" />
                          ) : (
                            <ImageIcon className="h-4 w-4 shrink-0 text-brand" />
                          )}
                          <span className="min-w-0 flex-1 truncate text-xs text-slate-700">
                            {file.name}
                          </span>
                          <span className="shrink-0 font-mono text-[0.65rem] text-slate-400">
                            {formatSize(file.size)}
                          </span>
                          <button
                            type="button"
                            onClick={() => removeFile(i)}
                            aria-label={`Remover ${file.name}`}
                            data-testid={`lead-modal-attachment-remove-${i}`}
                            className="shrink-0 rounded-full p-1 text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="lead-modal-submit-button"
                  className="h-10 rounded-full bg-brand text-sm font-semibold text-white transition-colors hover:bg-brand-700 sm:col-span-2"
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      Enviar solicitação
                      <Send className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>
    </LeadModalContext.Provider>
  );
};
